import React from 'react';
import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps,
  makeStyles,
  Checkbox
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import {useTranslation} from 'react-i18next';
import {
  INPUT_BORDER_COLOR,
  MENU_ITEM_BORDER_BOTTOM_COLOR
} from 'src/style/variable';

interface ISelectOptionItem {
  label: string;
  value: string | number;
  // eslint-disable-next-line camelcase
  secondary_label?: string;
}
const SELECT_ALL_VALUE = '__select_all__';

interface ISelectInputField<T extends ISelectOptionItem>
  extends Omit<SelectProps, 'error'> {
  label?: string;
  error?: Record<string, any>;
  itemList: T[];
  itemsHeaderLabel?: string;
  onClear?: () => void;
  renderMenuItem?: (item: T) => JSX.Element;
  noItemLabel: string;
  showSelectAll?: boolean;
}

const styles = makeStyles(() => ({
  selectInput: {
    '&.no-value': {
      '& .MuiSelect-select': {
        opacity: 0.5
      },
      '& .MuiSelect-icon': {
        opacity: 0.5
      }
    },
    '& .MuiSelect-select': {
      display: 'block'
    },
    '& .MuiSelect-select > div': {
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: INPUT_BORDER_COLOR
    }
  },
  selectMenuWrapper: {
    maxWidth: '32rem',
    minWidth: '28rem',
    '& .MuiMenuItem-root': {
      borderBottom: `1px solid ${MENU_ITEM_BORDER_BOTTOM_COLOR}`
    }
  },
  selectMenuItem: {
    fontSize: '1.2rem',
    paddingBlock: '0.75rem'
  },
  listItem: {
    '& .MuiListItemText-secondary': {
      fontSize: '0.9rem',
      marginTop: '5px',
      overflow: 'hidden',
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    },
    '& .MuiListItemText-primary': {
      fontSize: '1.2rem',
      overflow: 'hidden',
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    }
  },
  menuItem: {
    paddingBlock: 0
  },
  formControl: {
    paddingBottom: 0,
    marginBottom: 0
  },
  helperTextWrapper: {
    marginLeft: 0
  }
}));

const MultipleSelectInputField = <T extends ISelectOptionItem>(
  props: ISelectInputField<T>
): JSX.Element => {
  const {
    error,
    itemList = [],
    itemsHeaderLabel,
    label,
    renderMenuItem,
    onClear,
    className,
    noItemLabel = 'No Item',
    showSelectAll = false,
    onChange,
    ...rest
  } = props;
  const {placeholder} = rest;
  let {value} = rest;
  value = Array.isArray(value) ? value : [];
  const classes = styles();
  const {t} = useTranslation();

  const allOptionValues = itemList.map(item => item.value);
  const allSelected =
    itemList.length > 0 &&
    allOptionValues.every(optionValue => (value as any[]).includes(optionValue));
  const someSelected = (value as any[]).length > 0 && !allSelected;

  const handleSelectChange = (
    event: React.ChangeEvent<{name?: string; value: unknown}>,
    child: React.ReactNode
  ) => {
    const selected = event.target.value as Array<string | number>;

    if (showSelectAll && selected.includes(SELECT_ALL_VALUE)) {
      const nextValue = allSelected ? [] : allOptionValues;
      onChange?.(
        {
          ...event,
          target: {
            ...event.target,
            value: nextValue
          }
        } as React.ChangeEvent<{name?: string; value: unknown}>,
        child
      );
      return;
    }

    onChange?.(event, child);
  };

  const selectedOptions = itemList.filter(item =>
    (value as any[]).includes(item.value)
  );
  const renderValue = () => {
    const optionValueLabels: string[] = [];
    if (selectedOptions.length) {
      selectedOptions.forEach(item => {
        let valueLabel = '';
        if (item.label) {
          valueLabel += item.label;
          if (item.secondary_label) {
            valueLabel += ` (${item.secondary_label})`;
          }
        }
        if (valueLabel) optionValueLabels.push(valueLabel);
      });
      return optionValueLabels.join(', ');
    }
    return placeholder;
  };

  const extraDefaultProps: SelectProps = {};
  extraDefaultProps.renderValue = renderValue;

  return (
    <FormControl
      error={!!error}
      fullWidth={true}
      variant="outlined"
      className={classes.formControl}
      required={rest?.required}>
      {label && (
        <InputLabel
          style={{
            position: 'static',
            transform: 'none',
            color: '#241a2e',
            fontSize: '1.1rem',
            marginBottom: '0.5rem'
          }}
          shrink>
          {label}
        </InputLabel>
      )}
      <Select
        data-test-id="pcl-practices-patientState"
        fullWidth={true}
        variant="outlined"
        margin="dense"
        IconComponent={KeyboardArrowDownIcon}
        displayEmpty
        MenuProps={{
          PaperProps: {
            className: classes.selectMenuWrapper
          }
        }}
        className={`${classes.selectInput} ${
          !selectedOptions.length ? 'no-value' : ''
        } ${className}`}
        {...extraDefaultProps}
        {...rest}
        onChange={handleSelectChange}
        // Assigning empty string as default value. Because below we have used MenuItem with empty string as value to display Placeholder
        value={selectedOptions.length > 0 ? value : []}
        multiple>
        {onClear && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.5rem 1rem',
              pointerEvents: 'none'
            }}>
            <p
              style={{
                fontSize: '1.5rem',
                fontStyle: 'regular'
              }}>
              {itemsHeaderLabel ?? label}
            </p>
            {onClear && (
              <Button
                onClick={e => {
                  e.stopPropagation();
                  onClear();
                }}
                style={{pointerEvents: 'auto'}}>
                {t('COMMON.CLEAR_ALL')}
              </Button>
            )}
          </div>
        )}
        <Divider />
        <MenuItem value={''} style={{display: 'none'}}>
          {placeholder}
        </MenuItem>
        {showSelectAll && itemList.length > 0 && (
          <MenuItem value={SELECT_ALL_VALUE} className={classes.menuItem}>
            <ListItemText
              className={classes.listItem}
              primary={t('COMMON.SELECT_ALL')}
            />
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              color="primary"
            />
          </MenuItem>
        )}
        {itemList.length ? (
          itemList.map(item => {
            if (renderMenuItem) {
              return renderMenuItem(item);
            }
            return (
              <MenuItem
                key={item.value}
                value={item.value}
                className={classes.menuItem}>
                <ListItemText
                  className={classes.listItem}
                  primary={item.label}
                  secondary={item?.secondary_label ?? ''}
                />
                <Checkbox checked={(value as any[]).includes(item.value)} />
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>{noItemLabel ?? 'No Items'}</MenuItem>
        )}
      </Select>
      {error && (
        <FormHelperText className={classes.helperTextWrapper}>
          {t(error.message)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default MultipleSelectInputField;
